'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { siteSettings } from '@/data/siteData';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image?: string;
  slug: string;
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeCategory, setActiveCategory] = useState('Semua');

  // Fetch categories once on mount
  useEffect(() => {
    fetch('/api/categories')
      .then((res) => res.json())
      .then((data) => {
        const cats: string[] = data?.categories ?? [];
        setCategories(['Semua', ...cats]);
      })
      .catch(() => setCategories(['Semua']));
  }, []);

  // Fetch posts whenever search or category changes
  const fetchPosts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory !== 'Semua') params.set('category', activeCategory);
    if (search) params.set('search', search);
    const url = `/api/blog${params.toString() ? `?${params.toString()}` : ''}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        setPosts(Array.isArray(data?.posts) ? data.posts : []);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [activeCategory, search]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: 'linear-gradient(62deg, #8EC5FC, #E0C3FC)' }}
    >
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-[#48438e] font-bold text-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {siteSettings.name}
          </Link>
          <span className="text-sm text-gray-500 font-medium">Blog</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-[#48438e] mb-3">Blog & Artikel</h1>
          <p className="text-gray-600 max-w-xl mx-auto text-base">
            Tulisan seputar web development, tips coding, dan pengalaman sebagai developer.
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-xl mx-auto mb-8">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cari artikel..."
            className="w-full pl-12 pr-4 py-3 rounded-2xl border border-white/60 bg-white/80 backdrop-blur text-sm shadow focus:outline-none focus:ring-2 focus:ring-[#0090AD]"
          />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? 'bg-[#0090AD] text-white shadow-md'
                  : 'bg-white/70 text-gray-600 hover:bg-white hover:text-[#0090AD]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-sm text-gray-500 mb-6 text-center">
            Menampilkan <span className="font-semibold text-[#48438e]">{posts.length}</span> artikel
            {activeCategory !== 'Semua' && ` dalam kategori "${activeCategory}"`}
            {search && ` untuk "${search}"`}
          </p>
        )}

        {/* Loading Skeletons */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white/90 rounded-2xl shadow-sm overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-5/6" />
                  <div className="h-3 bg-gray-200 rounded w-1/3 mt-4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Articles Grid */}
        {!loading && posts.length === 0 && (
          <div className="text-center py-20">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 text-lg font-medium">Artikel tidak ditemukan</p>
            <p className="text-gray-400 text-sm mt-1">Coba kata kunci atau kategori lain</p>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <Link
                key={post.id}
                href={`/blog/${post.slug}`}
                className="group bg-white/90 backdrop-blur rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="relative h-48 overflow-hidden bg-gray-100">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#8EC5FC] to-[#E0C3FC]">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-[#0090AD] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h2 className="font-bold text-[#48438e] text-base leading-snug line-clamp-2 group-hover:text-[#0090AD] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-500 text-sm mt-2 line-clamp-3 flex-1">{post.excerpt}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-400">{formatDate(post.date)}</span>
                    <span className="text-xs text-[#0090AD] font-semibold group-hover:underline">Baca →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-8 mt-10 text-sm text-gray-500">
        © {new Date().getFullYear()} {siteSettings.name} · All rights reserved
      </footer>
    </div>
  );
}
